import { z } from 'zod';

export enum CustomTargetingOperator {
  Equal = 'EQUAL',
  NotEqual = 'NOT_EQUAL',
  Contains = 'CONTAINS',
  NotContains = 'NOT_CONTAINS',
}

export enum CustomTargetingParameter {
  Xp1 = 'xp1',
  Xp2 = 'xp2',
  Xp3 = 'xp3',
  Xp4 = 'xp4',
  Xp5 = 'xp5',
  Xp6 = 'xp6',
  Xp7 = 'xp7',
  Xp8 = 'xp8',
  Xp9 = 'xp9',
  Xp10 = 'xp10',
}

export enum LiverampGroupOperator {
  All = 'ALL',
  Any = 'ANY',
}

export enum LiverampKeyOperator {
  Include = 'INCLUDE',
  Exclude = 'EXCLUDE',
}

export enum LiverampVendor {
  LiveRamp = 'LIVE_RAMP',
}

export const customTargetingKeySchema = z.object({
  operator: z.enum(CustomTargetingOperator),
  xpParameter: z.enum(CustomTargetingParameter),
  value: z.string(),
});

export const customTargetingFormSchema = z.object({
  groups: z.array(z.object({ keys: z.array(customTargetingKeySchema) })),
});

export const liverampTargetingKeySchema = z.object({
  vendor: z.enum(LiverampVendor),
  operator: z.enum(LiverampKeyOperator),
  values: z.array(z.string()),
});

export const liverampTargetingFormSchema = z.object({
  groups: z.array(
    z.object({
      operator: z.enum(LiverampGroupOperator),
      keys: z.array(liverampTargetingKeySchema),
    }),
  ),
});

export type CustomTargetingFormValue = z.infer<typeof customTargetingFormSchema>;
export type LiverampTargetingFormValue = z.infer<typeof liverampTargetingFormSchema>;

export function createCustomTargetingKey(): CustomTargetingFormValue['groups'][number]['keys'][number] {
  return {
    operator: CustomTargetingOperator.Equal,
    xpParameter: CustomTargetingParameter.Xp1,
    value: '',
  };
}

export function createCustomTargetingGroup(): CustomTargetingFormValue['groups'][number] {
  return { keys: [createCustomTargetingKey()] };
}

export function createLiverampTargetingKey(): LiverampTargetingFormValue['groups'][number]['keys'][number] {
  return {
    vendor: LiverampVendor.LiveRamp,
    operator: LiverampKeyOperator.Include,
    values: [],
  };
}

export function createLiverampTargetingGroup(): LiverampTargetingFormValue['groups'][number] {
  return { operator: LiverampGroupOperator.All, keys: [createLiverampTargetingKey()] };
}

export function normalizeCustomTargeting(value: unknown): CustomTargetingFormValue {
  return customTargetingFormSchema.parse(value ?? { groups: [] });
}

export function normalizeLiverampTargeting(value: unknown): LiverampTargetingFormValue {
  if (!value || typeof value !== 'object' || !('groups' in value) || !Array.isArray(value.groups)) {
    return { groups: [] };
  }
  const normalized = {
    groups: value.groups.map((group) => {
      const record = asRecord(group);
      return {
        operator: record.operator ?? record.enforce ?? LiverampGroupOperator.All,
        keys: Array.isArray(record.keys)
          ? record.keys.map((key) => {
              const keyRecord = asRecord(key);
              const values = normalizeLiverampValues(keyRecord);
              return {
                vendor: keyRecord.vendor ?? LiverampVendor.LiveRamp,
                operator: keyRecord.operator ?? LiverampKeyOperator.Include,
                values,
              };
            })
          : [],
      };
    }),
  };
  const result = liverampTargetingFormSchema.safeParse(normalized);
  return result.success ? result.data : { groups: [] };
}

export function serializeAdvancedTargeting<T extends { groups: unknown[] }>(value: T): T | null {
  return value.groups.length ? value : null;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function normalizeLiverampValues(key: Record<string, unknown>): unknown[] {
  if (Array.isArray(key.values)) return key.values;
  if (typeof key.value === 'string' && key.value) return [key.value];
  return [];
}
